import * as THREE from 'three';
import type { ColourBlock } from '@vrlearning/lesson-schema';
import type { StatefulBlock } from './features.js';
import { setOpacity, type PartRegistry } from './parts.js';

type Painted = THREE.MeshStandardMaterial | THREE.MeshBasicMaterial;

/** A part's material for this block: a copy (decision 5); as light for `look: 'licht'` (sunbeams, a flame). */
function paintable(source: THREE.Material, look: ColourBlock['look']): Painted {
  if (look === 'licht') {
    const from = source as THREE.MeshStandardMaterial;
    return new THREE.MeshBasicMaterial({
      map: from.map ?? null,
      vertexColors: from.vertexColors,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
      fog: true,
    });
  }
  return source.clone() as Painted;
}

/** The colour block (V2): its parts' colour, own light and opacity, each following a state. */
export function buildColourBlock(block: ColourBlock, parts: PartRegistry): StatefulBlock {
  const materials: Painted[] = [];
  const now = {
    colour: block.colour,
    emissive: block.emissive,
    glow: block.glow,
    opacity: block.opacity,
  };
  const apply = (): void => {
    for (const material of materials) {
      material.color.set(now.colour);
      setOpacity(material, { base: now.opacity, light: block.look === 'licht' });
      if (material instanceof THREE.MeshStandardMaterial) {
        material.emissive.set(now.emissive);
        material.emissiveIntensity = now.glow;
      }
    }
  };
  parts.when(block.parts, (meshes) => {
    const copies = new Map<THREE.Material, Painted>();
    for (const mesh of meshes) {
      // The part's shared copies; the light look swaps them for unlit added ones (still shared).
      const given = parts.materialsOf(mesh);
      const own =
        block.look === 'licht'
          ? given.map((source) => {
              let copy = copies.get(source);
              if (!copy) {
                copy = paintable(source, block.look);
                copies.set(source, copy);
              }
              return copy;
            })
          : (given as Painted[]);
      if (block.look === 'licht') parts.replaceMaterials(mesh, own);
      for (const material of own) if (!materials.includes(material)) materials.push(material);
      if (block.look === 'licht') {
        mesh.castShadow = false;
        mesh.receiveShadow = false;
      }
    }
    apply();
  });
  return {
    set(setting, value) {
      if (setting === 'colour' || setting === 'emissive') {
        if (now[setting] === value) return;
        now[setting] = value as string;
      } else if (setting === 'glow' || setting === 'opacity') {
        if (now[setting] === value) return;
        now[setting] = value as number;
      }
      apply();
    },
  };
}
